import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { message, Button, Dropdown, Menu, Tooltip, Spin, Input } from 'antd';
import ZoomVideo from '@zoom/videosdk';
import { getDetailSession, getTokenSession, verifyJoinSession } from 'redux/actions/mvp/session.action';
import { fetchUserMvp } from 'redux/actions/user.action';
import './session-video-custom.scss';
import {
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  ShareAltOutlined,
  StopOutlined,
  MessageOutlined,
  PhoneOutlined,
  MoreOutlined,
  LoadingOutlined,
  UserOutlined,
  CloseOutlined,
} from '@ant-design/icons';

function SessionVideoCustom({ history, match, user, getDetailSession, getTokenSession, verifyJoinSession }) {
  const sessionId = match.params.id;
  const [client, setClient] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const userVideoRefs = useRef({});
  const shareCanvasRef = useRef(null);
  const shareVideoRef = useRef(null);
  const [shareRenderType, setShareRenderType] = useState('canvas'); // 'canvas' | 'video'
  const [activeShareUser, setActiveShareUser] = useState(null);
  const [pinnedUser, setPinnedUser] = useState(null);
  const [showUserList, setShowUserList] = useState(false); // Control user list panel visibility
  const [showChat, setShowChat] = useState(false); // Control chat panel visibility
  const [chatMessages, setChatMessages] = useState([]); // Zoom chat messages
  const [chatInput, setChatInput] = useState('');
  const [chatClient, setChatClient] = useState(null);
  const [chatToast, setChatToast] = useState(null); // { sender, text }

  const pinnedUserRef = useRef(null);
  const activeShareUserRef = useRef(null);
  const chatToastTimeoutRef = useRef(null);

  // Video resolution constant
  const RESOLUTION = { width: 640, height: 360 };

  useEffect(() => {
    pinnedUserRef.current = pinnedUser;
  }, [pinnedUser]);

  useEffect(() => {
    activeShareUserRef.current = activeShareUser;
  }, [activeShareUser]);

  const joinSession = async () => {
    setLoading(true);
    try {
      const sessionInfo = await getDetailSession({ sessionId });
      if (!sessionInfo) throw new Error('No session info');

      const verifyData = await verifyJoinSession({
        sessionId: sessionInfo.sessionUserId,
        sessionUserId: sessionInfo.sessionUserId,
      });
      if (!verifyData || verifyData.status !== 'OK') {
        throw new Error('Unable to join session. Please check your permission or session status.');
      }

      let infoZoom = verifyData.infoZoom;
      if (typeof infoZoom === 'string') infoZoom = JSON.parse(infoZoom);
      if (!infoZoom) throw new Error('No Zoom info');

      const tokenData = await getTokenSession({ sessionName: infoZoom.sessionName });
      const token = typeof tokenData === 'string' ? tokenData : tokenData?.token || tokenData?.data;
      if (!token) throw new Error('No token');
      const userName = user?.name || 'Anonymous User';
      const sessionPasscode = infoZoom.password || '';

      const client = ZoomVideo.createClient();
      await client.init('en-US', 'Global');
      // Leave any existing session to prevent "duplicated operation" error
      client.leave();
      await client.join(infoZoom.sessionName, token, userName, sessionPasscode);
      setClient(client);

      // Init chat client after join
      try {
        const chat = client.getChatClient();
        setChatClient(chat);

        // Optionally load existing chat history
        try {
          const history = await chat.getHistory();
          if (Array.isArray(history)) {
            const currentUserId = client.getCurrentUserInfo()?.userId;
            const mapped = history
              .filter((item) => item && (item.message || item.content))
              .map((item, idx) => ({
                id: item.id || `${item.timestamp || ''}-${idx}`,
                sender:
                  item.sender?.displayName ||
                  item.sender?.userName ||
                  item.sender?.userId ||
                  'Unknown',
                text: item.message || item.content || '',
                timestamp: item.timestamp || new Date().toISOString(),
                isSelf: item.sender?.userId === currentUserId,
              }));
            setChatMessages(mapped);
          }
        } catch (e) {
          console.warn('Error getting history:', e);
        }
      } catch (e) {
        console.warn('Chat client not available:', e);
      }

      // Start local media (video & audio)
      const stream = client.getMediaStream();
      try {
        await stream.startVideo();
      } catch (e) {
        setIsVideoEnabled(false);
      }

      try {
        await stream.startAudio();
      } catch (e) {
        setIsAudioEnabled(false);
      }

      const users = client.getAllUser();
      setAllUsers(users);

      users.forEach((user) => {
        if (user.sharerOn) {
          setActiveShareUser(user);
          if (shareCanvasRef.current) {
            stream.startShareView(shareCanvasRef.current, user.userId);
          }
        }
      });

      // Listen for remote user join and video events
      client.on('user-added', (payload) => {
        // const currentUserId = client.getCurrentUserInfo().userId;
        // payload.forEach((item) => {
        //   if (item.userId !== currentUserId) message.info(`${item.displayName} joined the session.`);
        // });
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('user-removed', (payload) => {
        payload.forEach((item) => {
          // message.info(`${item.displayName} left the session.`);

          if (pinnedUserRef.current && pinnedUserRef.current.userId === item.userId) {
            setPinnedUser(null);
          }

          if (activeShareUserRef.current && activeShareUserRef.current.userId === item.userId) {
            setActiveShareUser(null);
            setShareRenderType('canvas');
            try {
              const stream = client.getMediaStream();
              stream.stopShareView();
            } catch (e) {
              console.warn('Could not stop share view:', e);
            }
          }
        });
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('user-updated', (payload) => {
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('passively-stop-share', (payload) => {
        setActiveShareUser(null);
        setShareRenderType('canvas');
      });
    } catch (e) {
      message.error(e.message || 'Failed to join session');
      history.push('/');
    } finally {
      setLoading(false);
    }
  };

  // Tự động join session khi vào trang
  useEffect(() => {
    joinSession();
  }, []);

  // Rời session
  const leaveSession = async () => {
    if (client) {
      await client.leave();
      setClient(null);
      history.push(`/session-detail/${sessionId}`);
    }
  };

  // Bật/tắt audio
  const toggleAudio = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const currentUserId = client.getCurrentUserInfo().userId;

    if (isAudioEnabled) {
      try {
        await stream.muteAudio();
        setIsAudioEnabled(false);
        // Update user's muted status in allUsers
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: true } : user)),
        );
      } catch (e) {
        console.warn('Could not mute audio:', e);
        message.warning('Could not mute audio. Please check your microphone permissions.');
      }
    } else {
      try {
        await stream.unmuteAudio();
        setIsAudioEnabled(true);
        // Update user's muted status in allUsers
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: false } : user)),
        );
      } catch (e) {
        console.warn('Could not unmute audio:', e);
        message.warning('Could not unmute audio. Please check your microphone permissions.');
      }
    }
  };

  // Helper: Start local video and attach to container
  const startVideo = async (stream, userId) => {
    try {
      await stream.startVideo();
      let userVideo = await stream.attachVideo(userId, RESOLUTION);
      document.querySelector('video-player-container').appendChild(userVideo);
    } catch (e) {
      console.warn('Could not start video:', e);
      message.warning('Could not start video. Please check your camera permissions.');
      throw e; // Re-throw to let toggleVideo handle it
    }
  };

  // Helper: Start remote video and attach to container
  const startUserVideo = async (stream, userId, ref) => {
    if (!stream || !userId || !ref) {
      return;
    }
    // Detach video trước nếu đã từng attach
    try {
      stream.detachVideo(userId);
    } catch (e) {
      // ignore nếu chưa từng attach
    }
    ref.innerHTML = '';
    try {
      let userVideo = await stream.attachVideo(userId, RESOLUTION);
      ref.appendChild(userVideo);
    } catch (e) {
      console.warn(`Could not attach video for user ${userId}:`, e);
    }
  };

  // Helper: Stop local video and detach from container
  const stopVideo = async (stream, userId) => {
    try {
      await stream.stopVideo();
      stream.detachVideo(userId);
    } catch (e) {
      console.warn('Could not stop video:', e);
    }
  };

  // Bật/tắt video
  const toggleVideo = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const userId = client.getCurrentUserInfo().userId;
    if (isVideoEnabled) {
      try {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      } catch (e) {
        console.warn('Could not stop video:', e);
        message.warning('Could not stop video. Please check your camera permissions.');
      }
    } else {
      try {
        await startVideo(stream, userId);
        setIsVideoEnabled(true);
      } catch (e) {
        console.warn('Could not start video:', e);
        // Don't show error message here as startVideo already shows it
      }
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    allUsers.forEach((user) => {
      if (!userVideoRefs.current[user.userId]) {
        return;
      }
      startUserVideo(stream, user.userId, userVideoRefs.current[user.userId]);
    });
  }, [allUsers, client, pinnedUser]);

  // share screen
  const handleStartShare = async () => {
    if (!client) return;
    const currentUser = client.getCurrentUserInfo();
    setActiveShareUser(currentUser);

    const stream = client.getMediaStream();
    const userId = currentUser.userId;

    // Tự động tắt camera khi bật sharing
    if (isVideoEnabled) {
      try {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      } catch (e) {
        console.warn('Could not stop video when starting share:', e);
      }
    }

    try {
      if (stream.isStartShareScreenWithVideoElement()) {
        setShareRenderType('video');
        await stream.startShareScreen(shareVideoRef.current, { enableOptimizeForSharedVideo: true });
      } else {
        setShareRenderType('canvas');
        await stream.startShareScreen(shareCanvasRef.current, { enableOptimizeForSharedVideo: true });
      }
    } catch (e) {
      message.error(e.message || 'Failed to start screen share');
    }
  };

  // stop share screen
  const handleStopShare = async () => {
    if (!client) return;
    setActiveShareUser(null);
    setShareRenderType('canvas');
    const stream = client.getMediaStream();
    try {
      await stream.stopShareScreen();
    } catch (e) {
      message.error(e.message || 'Failed to stop screen share');
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    const onActiveShareChange = (payload) => {
      if (payload.state === 'Active') {
        const user = allUsers.find((u) => u.userId === payload.userId);
        setActiveShareUser(user || { userId: payload.userId });
        if (shareCanvasRef.current) {
          stream.startShareView(shareCanvasRef.current, payload.userId);
        }
      } else if (payload.state === 'Inactive') {
        setActiveShareUser(null);
        setShareRenderType('canvas');
        stream.stopShareView();
      }
    };
    client.on('active-share-change', onActiveShareChange);
    return () => {
      client.off('active-share-change', onActiveShareChange);
    };
  }, [client, allUsers]);

  const renderUserBox = (user, isMain = false) => {
    const isPinned = pinnedUser && pinnedUser.userId === user.userId;
    const menu = (
      <Menu>
        <Menu.Item key="pin" onClick={() => setPinnedUser(isPinned ? null : user)}>
          {isPinned ? 'Unpin screen' : 'Pin screen'}
        </Menu.Item>
      </Menu>
    );
    return (
      <div key={user.userId} className={`session-video-user-box${isMain ? ' main' : ''}`}>
        <Dropdown overlay={menu} trigger={['click']}>
          <Button shape="circle" icon={<MoreOutlined />} size="small" className="session-video-user-menu" />
        </Dropdown>
        <div className="session-video-username">
          {user.displayName || user.userId}
          {user.userId === client?.getCurrentUserInfo()?.userId ? ' (You)' : ''}
          <span className="user-status-icon audio">
            <AudioOutlined
              style={{
                color: user.muted ? '#e53935' : '#4caf50',
                fontSize: 16,
              }}
            />
            {user.muted && <span className="icon-slash" />}
          </span>
        </div>
        {user.isVideoOn || user.bVideoOn ? (
          <video-player-container
            ref={(el) => (userVideoRefs.current[user.userId] = el)}
            className="session-video-player"
          ></video-player-container>
        ) : (
          <div className="session-video-avatar-icon">
            <UserOutlined style={{ fontSize: 40, color: '#fff' }} />
          </div>
        )}
      </div>
    );
  };

  // Render user list item for the side panel
  const renderUserListItem = (user) => {
    const isPinned = pinnedUser && pinnedUser.userId === user.userId;
    const isCurrentUser = user.userId === client?.getCurrentUserInfo()?.userId;

    const menu = (
      <Menu>
        <Menu.Item key="pin" onClick={() => setPinnedUser(isPinned ? null : user)}>
          {isPinned ? 'Unpin screen' : 'Pin screen'}
        </Menu.Item>
      </Menu>
    );

    return (
      <div key={user.userId} className="user-list-item">
        <div className="user-item-name">
          <div className="user-item-avatar-icon">
            <UserOutlined style={{ fontSize: 16, color: '#333' }} />
          </div>
          {user.displayName || user.userId}
          {isCurrentUser && ' (You)'}
        </div>
        <div className="user-item-status">
          <span className="user-status-icon audio">
            <AudioOutlined
              style={{
                color: user.muted ? '#e53935' : '#4caf50',
              }}
            />
            {user.muted && <span className="icon-slash" />}
          </span>
          <span className="user-status-icon video">
            <VideoCameraOutlined
              style={{
                color: user.isVideoOn || user.bVideoOn ? '#4caf50' : '#e53935',
              }}
            />
            {!(user.isVideoOn || user.bVideoOn) && <span className="icon-slash" />}
          </span>
          {isPinned && (
            <span className="pin-indicator" style={{ color: '#1890ff', fontSize: 12 }}>
              📌
            </span>
          )}
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} size="small" className="user-item-menu-button" />
          </Dropdown>
        </div>
      </div>
    );
  };

  const mainContent = pinnedUser || activeShareUser;
  const currentUserId = client?.getCurrentUserInfo()?.userId;
  const isCurrentUserSharing = activeShareUser && activeShareUser.userId === currentUserId;
  const isAnyoneSharing = !!activeShareUser;
  const isOtherUserSharing = isAnyoneSharing && !isCurrentUserSharing;

  // Clear chat toast timeout on unmount
  useEffect(() => {
    return () => {
      if (chatToastTimeoutRef.current) {
        clearTimeout(chatToastTimeoutRef.current);
      }
    };
  }, []);

  // Listen for incoming Zoom chat messages
  useEffect(() => {
    if (!client) return;

    const onChatMessage = (payload) => {
      try {
        const currentUserIdInner = client.getCurrentUserInfo()?.userId;
        const senderInfo = payload?.sender || {};
        const text = payload?.message || payload?.content || '';
        if (!text) return;

        const isSelf = senderInfo.userId === currentUserIdInner;
        const senderName = isSelf
          ? 'You'
          : senderInfo.name || senderInfo.userId || senderInfo.userGuid || 'Unknown';

        setChatMessages((prev) => [
          ...prev,
          {
            id: payload.id || payload.msgId || Date.now(),
            sender: senderName,
            text,
            timestamp: payload.timestamp || new Date().toISOString(),
            isSelf,
          },
        ]);

        // Nếu panel chat đang đóng và đây là tin nhắn từ người khác -> show custom toast
        if (!isSelf && !showChat) {
          if (chatToastTimeoutRef.current) {
            clearTimeout(chatToastTimeoutRef.current);
          }
          setChatToast({
            sender: senderName,
            text,
          });
          chatToastTimeoutRef.current = setTimeout(() => {
            setChatToast(null);
          }, 4000);
        }
      } catch (e) {
        console.warn('Error handling chat-on-message payload:', e);
      }
    };

    try {
      client.on('chat-on-message', onChatMessage);
    } catch (e) {
      console.warn('Failed to bind chat-on-message listener on client:', e);
    }

    return () => {
      try {
        client.off('chat-on-message', onChatMessage);
      } catch (e) {
        // ignore
      }
    };
  }, [client, showChat]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    const text = chatInput.trim();
    setChatInput('');

    if (chatClient) {
      try {
        // Send to everyone in the session
        await chatClient.sendToAll(text);
      } catch (e) {
        console.warn('Failed to send chat via Zoom:', e);
        message.error(e.message || 'Failed to send message');
      }
    }
  };

  return (
    <div className="session-video-wrapper">
      <div className="session-video-main-layout">
        <div className={`session-video-main-section ${mainContent ? 'has-main' : 'no-main'}`}>
          {pinnedUser &&
            (() => {
              const fullPinnedUser = allUsers.find((user) => user.userId === pinnedUser.userId);
              return fullPinnedUser ? renderUserBox(fullPinnedUser, true) : null;
            })()}
          <div
            className={`session-video-share-container ${pinnedUser ? 'has-pin' : 'no-pin'} ${
              activeShareUser ? 'has-share' : 'no-share'
            }`}
          >
            <canvas
              ref={shareCanvasRef}
              id="users-screen-share-content-canvas"
              width={1920}
              height={1080}
              className={`session-video-share-canvas ${shareRenderType === 'canvas' && activeShareUser ? ' show' : ''}`}
            />
            <video
              ref={shareVideoRef}
              id="my-screen-share-content-video"
              width={960}
              height={540}
              className={`session-video-share-video ${shareRenderType === 'video' && activeShareUser ? ' show' : ''}`}
              autoPlay
              playsInline
              muted
            />
          </div>
        </div>
        <div className={`session-video-side-section ${mainContent ? 'has-main' : 'no-main'}`}>
          <div
            className={`${mainContent ? 'session-video-sidebar' : 'session-video-users-list'} ${
              pinnedUser ? 'has-pin' : 'no-pin'
            } ${activeShareUser ? 'has-share' : 'no-share'}`}
          >
            {allUsers.filter((u) => !pinnedUser || u.userId !== pinnedUser.userId).map((user) => renderUserBox(user))}
          </div>
        </div>
      </div>

      {/* User List Panel */}
      {showUserList && (
        <div className="user-list-panel">
          <div className="user-list-header">
            <h5>Participants</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowUserList(false)}
              className="close-button"
            />
          </div>
          {allUsers.map((user) => renderUserListItem(user))}
        </div>
      )}

      {/* Chat Panel */}
      {showChat && (
        <div className="chat-panel">
          <div className="chat-header">
            <h5>Chat</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowChat(false)}
              className="close-button"
            />
          </div>
          <div className="chat-messages">
            {chatMessages.length === 0 && (
              <div className="chat-empty">No messages yet. Start the conversation!</div>
            )}
            {chatMessages.map((item, index) => {
              const prev = chatMessages[index - 1];
              const showSender = !prev || prev.sender !== item.sender || prev.isSelf !== item.isSelf;

              return (
                <div
                  key={item.id}
                  className={`chat-message ${item.isSelf ? 'self' : 'other'}`}
                >
                  {showSender && (
                    <div className="chat-message-meta">
                      <span className="chat-sender">{item.sender}</span>
                    </div>
                  )}
                  <div className="chat-message-text">{item.text}</div>
                </div>
              );
            })}
          </div>
          <div className="chat-input-wrapper">
            <Input.TextArea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Type a message..."
              autoSize={{ minRows: 1, maxRows: 3 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendChat();
                }
              }}
            />
            <Button type="primary" onClick={handleSendChat} disabled={!chatInput.trim()}>
              Send
            </Button>
          </div>
        </div>
      )}

      <div className="session-video-controls-bar">
        <Tooltip title={isAudioEnabled ? 'Mute Audio' : 'Unmute Audio'}>
          <Button
            onClick={toggleAudio}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <AudioOutlined />
                {!isAudioEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn audio-toggle-btn"
          />
        </Tooltip>
        <Tooltip title={isVideoEnabled ? 'Stop Video' : 'Start Video'}>
          <Button
            onClick={toggleVideo}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <VideoCameraOutlined />
                {!isVideoEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn video-toggle-btn"
          />
        </Tooltip>
        {!isAnyoneSharing && (
          <Tooltip title="Share Screen">
            <Button onClick={handleStartShare} style={{ marginRight: 8 }} icon={<ShareAltOutlined />} />
          </Tooltip>
        )}
        {isCurrentUserSharing && (
          <Tooltip title="Stop Sharing">
            <Button onClick={handleStopShare} style={{ marginRight: 8 }} icon={<StopOutlined />} danger />
          </Tooltip>
        )}
        {isOtherUserSharing && (
          <Tooltip title="Screen sharing in progress" placement="top">
            <Button style={{ marginRight: 8 }} icon={<ShareAltOutlined />} disabled />
          </Tooltip>
        )}
        <Tooltip title="Show Participants">
          <Button
            onClick={() => {
              const next = !showUserList;
              setShowUserList(next);
              if (next) setShowChat(false);
            }}
            style={{ marginRight: 8 }}
            icon={<UserOutlined />}
            type={showUserList ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title={showChat ? 'Close Chat' : 'Open Chat'}>
          <Button
            onClick={() => {
              const next = !showChat;
              setShowChat(next);
              if (next) setShowUserList(false);
            }}
            style={{ marginRight: 8 }}
            icon={<MessageOutlined />}
            type={showChat ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title="Leave Session">
          <Button danger onClick={leaveSession} icon={<PhoneOutlined />} />
        </Tooltip>
      </div>
      {chatToast && (
        <div
          className="chat-toast"
          onClick={() => {
            setShowChat(true);
            setShowUserList(false);
            setChatToast(null);
          }}
        >
          <div className="chat-toast-sender">{chatToast.sender}</div>
          <div className="chat-toast-text">{chatToast.text}</div>
        </div>
      )}
      {loading && (
        <div className="session-video-loading-overlay">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = { getDetailSession, getTokenSession, fetchUserMvp, verifyJoinSession };
export default connect(mapStateToProps, mapDispatchToProps)(SessionVideoCustom);
